import React, { useEffect, useState } from 'react';
import NavBar from '../common/NavBar';
import Footer from '../common/Footer';
import GuestsCalendar from './GuestsModals/GuestsCalendar';
import GuestMenu from './GuestMenu';
import { useTranslation } from 'react-i18next';

const GuestsBookings = () => {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [apartments, setApartments] = useState({});
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [startDate] = useState("");
  const [endDate] = useState("");
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null); // Track which booking is being deleted

  // Helper function to check if booking has started
  const hasBookingStarted = (startDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to midnight for date comparison
    const bookingStart = new Date(startDate);
    return today >= bookingStart;
  };

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || "http://localhost:8000";
  };

  useEffect(() => {
    const storedUserId = localStorage.getItem("user_id");
    if (storedUserId) {
      fetchBookings(storedUserId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBookings = async (guestId) => {
    try {
      const token = localStorage.getItem("token");
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", `Token ${token}`);

      const response = await fetch(
        `${getApiBaseUrl()}/api/bookings/?guest_id=${encodeURIComponent(guestId)}`,
        {
          method: "GET",
          headers: myHeaders,
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch bookings (Status: ${response.status})`);
      }

      const bookingsData = await response.json();

      const apartmentsResponse = await fetch(`${getApiBaseUrl()}/api/properties/apartments/`, {
        method: "GET",
        headers: myHeaders,
      });

      if (!apartmentsResponse.ok) {
        throw new Error(`Failed to fetch apartments (Status: ${apartmentsResponse.status})`);
      }

      const apartmentsData = await apartmentsResponse.json();
      const apartmentsMap = {};
      apartmentsData.forEach((apartment) => {
        apartmentsMap[apartment.id] = apartment.apartment_name;
      });
      setApartments(apartmentsMap);

      setBookings(bookingsData);
    } catch (error) {
      console.error("Error fetching guest bookings:", error);
      setError(t("error_fetching_bookings"));
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm(t("confirm_delete_booking"))) return;
    setDeletingId(bookingId);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${getApiBaseUrl()}/api/bookings/${bookingId}/`, {
        method: "DELETE",
        headers: {
          "Authorization": `Token ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(t("failed_to_delete_booking"));
      }
      setBookings((prev) => prev.filter((b) => b.id !== bookingId));
    } catch (error) {
      console.error("Error deleting booking:", error);
      setError(t("failed_to_delete_booking"));
    } finally {
      setDeletingId(null);
    }
  };

  /*
  const openGuestsCalendar = () => {
    setIsCalendarOpen(true);
  };
  */

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <GuestMenu />
      <div className="flex flex-col items-center mt-16">
        <h1 className="text-3xl font-bold mb-8">{t("bookings")}</h1>

        {isCalendarOpen && (
          <GuestsCalendar
            isOpen={isCalendarOpen}
            onClose={() => setIsCalendarOpen(false)}
            //apartmentId={apartmentId}
            startDate={startDate}
            endDate={endDate}
            apartments={apartments}
          />
        )}

        {bookings.length === 0 ? (
          <p className="text-red-600 font-semibold mb-4">
            {t("no_bookings_found")}
          </p>
        ) : (
          <div className="w-full max-w-4xl overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-300">
              <thead>
                <tr className="bg-gray-100 text-center">
                  <th className="py-3 px-4 border-b">{t("apartment_name")}</th>
                  <th className="py-3 px-4 border-b">{t("start_date")}</th>
                  <th className="py-3 px-4 border-b">{t("end_date")}</th>
                  <th className="py-3 px-4 border-b">{t("action")}</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => {
                  const isStarted = hasBookingStarted(booking.start_date);
                  return (
                    <tr key={booking.id} className="hover:bg-gray-50 text-center">
                      <td className="py-3 px-4 border-b">
                        {apartments[booking.apartment] || t("unknown_apartment")}
                      </td>
                      <td className="py-3 px-4 border-b">{booking.start_date}</td>
                      <td className="py-3 px-4 border-b">{booking.end_date}</td>
                      <td className="py-3 px-4 border-b">
                        <button
                          onClick={() => handleDeleteBooking(booking.id)}
                          disabled={deletingId === booking.id || isStarted}
                          className={`px-3 py-1 rounded ${
                            deletingId === booking.id || isStarted
                              ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                              : 'bg-red-500 text-white hover:bg-red-700'
                          }`}
                          title={isStarted ? t("booking_started_cant_delete") : ""}
                        >
                          {deletingId === booking.id ? t("deleting") : t("delete")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {error && (
              <p className="text-red-600 font-semibold mt-4">{error}</p>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default GuestsBookings;